import React, {useContext, useEffect, useState, useCallback, useMemo} from "react";
import {FormattedMessage, injectIntl} from "react-intl";
import Column from "@amzn/meridian/column";
import Row from "@amzn/meridian/row";
import Text from "@amzn/meridian/text";
import Select from "@amzn/meridian/select";
import SelectOption from "@amzn/meridian/select/select-option";
import Input from "@amzn/meridian/input";
import Button from "@amzn/meridian/button";
import get from "lodash/get";
import {StationConfigContext} from "../../context/StationConfigContext";
import {getNodeId} from "../../Utility";
import {STRINGS} from "../../Constants";
import {getPathSchedule, setPathSchedule, deletePathSchedule, getSortPathInfoList}
    from "../../resources/PathScheduleResource";
import {fetchSortPathConfigurations} from "../../resources/SortPathSettingsResource";
import {SCHEDULE_MODAL_MODE, formStateFromEntry, buildSetScheduleArgs, toDateInputValue} from "../../helper/PathScheduleHelper";
import PathScheduleList from "./PathScheduleList";
import AddScheduleModal from "./AddScheduleModal";

/**
 * Top-level container for the Path Schedule tab.
 *
 * Implements the per-cycle retrieval workflow (design Approach A): schedules are
 * fetched on tab load, whenever the cycle selection changes, and whenever the
 * optional date-range filter changes. GetPathSchedule is scoped to a single
 * station+cycle, so every cycle switch issues a fresh request.
 *
 * Workstream 2 adds the schedule editor: an AddScheduleModal supporting create,
 * edit-active, edit-scheduled, and activate-now flows, plus scheduled-entry delete.
 * Path/config option data (GetSortPathInfoList + SortPathSettings) is lazily fetched
 * the first time the modal is opened.
 */
const PathScheduleTab = (props) => {
    const {intl} = props;
    const {state} = useContext(StationConfigContext);

    const cycleList = Array.isArray(state.cycleList) ? state.cycleList : [];

    const [selectedCycleId, setSelectedCycleId] = useState(undefined);
    const [fromDate, setFromDate] = useState(undefined);
    const [toDate, setToDate] = useState(undefined);
    const [dataVersion, setDataVersion] = useState(0);

    const [activeSchedule, setActiveSchedule] = useState(undefined);
    const [scheduled, setScheduled] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(undefined);

    // Editor (modal) state
    const [modalOpen, setModalOpen] = useState(false);
    const [modalMode, setModalMode] = useState(SCHEDULE_MODAL_MODE.CREATE);
    const [modalInitialForm, setModalInitialForm] = useState(undefined);
    const [editingEffectiveDate, setEditingEffectiveDate] = useState(undefined);
    const [isSaving, setIsSaving] = useState(false);
    const [saveError, setSaveError] = useState(undefined);

    // Option data for the modal (lazy-loaded on first open, cached by dataVersion)
    const [sortPathInfoList, setSortPathInfoList] = useState([]);
    const [sortPathSettings, setSortPathSettings] = useState([]);
    const [optionsLoaded, setOptionsLoaded] = useState(false);

    // PathSchedule keys entries by the cycle *name* (e.g., "CYCLE_1"), matching how
    // SortPathSetting/SortPathInfo and the reservation/constraints flows identify a cycle.
    // The backend partition is {stationCode}_{cycleName}, so the value sent must be
    // the cycle name, not the cycle UUID.
    const getCycleName = (stationCycle) => get(stationCycle, ["cycle", "cycleName"]);

    // Display cycles in sorted order (by cycle name). The same sorted list drives both
    // the dropdown render and the auto-select below, so the auto-selected top entry is
    // the first cycle in sorted order (not raw backend order). localeCompare orders the
    // names naturally and consistently.
    const sortedCycleList = useMemo(
        () => cycleList.slice().sort((a, b) =>
            getCycleName(a).localeCompare(getCycleName(b))),
        [cycleList]);

    // Default the cycle selection to the first cycle (in sorted order) once available.
    useEffect(() => {
        if (!selectedCycleId && sortedCycleList.length > 0) {
            setSelectedCycleId(getCycleName(sortedCycleList[0]));
        }
    }, [sortedCycleList, selectedCycleId]);

    // Fetch whenever the selected cycle, date range, or dataVersion (post-mutation refresh) changes.
    // Uses an ignore flag to guard against stale responses when the user switches cycles
    // or edits dates while a request is still in-flight.
    useEffect(() => {
        let ignore = false;
        if (!selectedCycleId) {
            return undefined;
        }
        setIsLoading(true);
        setError(undefined);
        getPathSchedule(getNodeId(), selectedCycleId, fromDate, toDate)
            .then((data) => {
                if (ignore) return;
                // Error detection keys off the errorResponse shape (errorCode), not the mere
                // presence of a `message` field — a successful GetPathScheduleOutput could carry
                // an informational message without being an error.
                if (!data || data.errorCode) {
                    setActiveSchedule(undefined);
                    setScheduled([]);
                    setError((data && data.message) ? data.message : STRINGS.RETRY_LATER);
                } else {
                    setActiveSchedule(data.activeSchedule);
                    setScheduled(Array.isArray(data.stagedSchedules) ? data.stagedSchedules : []);
                }
            }, (err) => {
                if (ignore) return;
                setActiveSchedule(undefined);
                setScheduled([]);
                setError(err || STRINGS.RETRY_LATER);
            })
            .finally(() => {
                if (!ignore) setIsLoading(false);
            });
        return () => { ignore = true; };
    }, [selectedCycleId, fromDate, toDate, dataVersion]);

    const refreshSchedules = useCallback(() => setDataVersion((v) => v + 1), []);

    // Station-local "now" used for future-date validation and Activate Now's effective date.
    // TODO: source the station's IANA timezone from an SCC/station-config source instead of
    // browser-local time (design Open Question). Interim: browser-local date (YYYY-MM-DD),
    // derived via toDateInputValue so it matches the effective-date input's local-date parts
    // (avoids a near-midnight off-by-one vs a UTC toISOString date).
    const stationNow = toDateInputValue(new Date());

    // Lazily load path/config option data the first time the editor is opened.
    // optionsLoaded is set only after BOTH fetches succeed, so any failure leaves the
    // guard false and the next modal open retries (otherwise the Fill/Pick selectors
    // would stay empty for the rest of the session).
    //
    // These resources resolve with an errorResponse object (not a reject) on a backend
    // error, so a bad/empty-shaped payload must be treated as a failure explicitly —
    // throwing here routes it to the Promise.all rejection path (guard stays false) and
    // surfaces an error instead of silently latching optionsLoaded=true with empty lists.
    const ensureOptionsLoaded = useCallback(() => {
        if (optionsLoaded) {
            return;
        }
        Promise.all([
            getSortPathInfoList(getNodeId(), true).then((data) => {
                if (!data || !Array.isArray(data.sortPathInfoList)) {
                    throw new Error("GetSortPathInfoList failed");
                }
                setSortPathInfoList(data.sortPathInfoList);
            }),
            fetchSortPathConfigurations(getNodeId()).then((data) => {
                if (!data || !Array.isArray(data.sortPathSettings)) {
                    throw new Error("FetchSortPathConfigurations failed");
                }
                setSortPathSettings(data.sortPathSettings);
            })
        ]).then(
            () => setOptionsLoaded(true),
            () => setSaveError(STRINGS.RETRY_LATER) // leave optionsLoaded false so the next open retries
        );
    }, [optionsLoaded]);

    const openModal = (mode, entry) => {
        setModalMode(mode);
        setSaveError(undefined);
        setEditingEffectiveDate(entry ? get(entry, ["effectiveDate"]) : undefined);
        setModalInitialForm(entry ? formStateFromEntry(entry) : undefined);
        ensureOptionsLoaded();
        setModalOpen(true);
    };

    const onClickAdd = () => openModal(SCHEDULE_MODAL_MODE.CREATE, undefined);
    const onEditActive = (entry) => openModal(SCHEDULE_MODAL_MODE.EDIT_ACTIVE, entry);
    const onEditScheduled = (entry) => openModal(SCHEDULE_MODAL_MODE.EDIT_STAGED, entry);
    const onActivateNow = (entry) => openModal(SCHEDULE_MODAL_MODE.ACTIVATE_NOW, entry);

    const onSave = (formResult) => {
        setIsSaving(true);
        setSaveError(undefined);
        const args = buildSetScheduleArgs(formResult);
        setPathSchedule(getNodeId(), selectedCycleId, args.effectiveDate,
            args.defaultPathConfig, args.dayOfWeekOverrides, args.version)
            .then((data) => {
                if (!data || data.errorCode) {
                    setSaveError((data && data.message) ? data.message : STRINGS.RETRY_LATER);
                } else {
                    setModalOpen(false);
                    refreshSchedules();
                }
            }, (err) => setSaveError(err || STRINGS.RETRY_LATER))
            .finally(() => setIsSaving(false));
    };

    const onDeleteScheduled = (entry) => {
        // eslint-disable-next-line no-restricted-globals, no-alert
        const confirmed = typeof window !== "undefined" && window.confirm
            ? window.confirm(intl.formatMessage({id: "pathSchedule.confirmDelete",
                defaultMessage: "Delete this scheduled entry?"}))
            : true;
        if (!confirmed) {
            return;
        }
        deletePathSchedule(getNodeId(), selectedCycleId, get(entry, ["effectiveDate"]), get(entry, ["version"]))
            .then((data) => {
                // deletePathSchedule resolves with the errorResponse on a backend failure
                // (it does not reject), so inspect the resolved value before refreshing —
                // otherwise a failed delete would silently refresh against stale state.
                if (!data || data.errorCode) {
                    setError((data && data.message) ? data.message : STRINGS.RETRY_LATER);
                } else {
                    refreshSchedules();
                }
            }, (err) => setError(err || STRINGS.RETRY_LATER));
    };

    const onClearDateRange = () => {
        setFromDate(undefined);
        setToDate(undefined);
    };

    const renderControls = () => (
        <Row spacing={"medium"} alignmentVertical={"bottom"} wrap={"down"}>
            <Column spacing={"xsmall"}>
                <Text type="b200" color="secondary">
                    <FormattedMessage id="pathSchedule.selectCycle" defaultMessage="Cycle (CPT)"/>
                </Text>
                <Select value={selectedCycleId}
                        onChange={setSelectedCycleId}
                        placeholder={intl.formatMessage({id: "pathSchedule.selectCycle", defaultMessage: "Cycle (CPT)"})}
                        width={260}
                        size="small">
                    {sortedCycleList.map((stationCycle) => {
                        const name = getCycleName(stationCycle);
                        return (
                            <SelectOption key={name} value={name} label={name}/>
                        );
                    })}
                </Select>
            </Column>
            <Column spacing={"xsmall"}>
                <Text type="b200" color="secondary">
                    <FormattedMessage id="pathSchedule.fromDate" defaultMessage="From date"/>
                </Text>
                <Input type="date" value={fromDate || ""}
                       onChange={(value) => setFromDate(value || undefined)}
                       size="small" width={180}/>
            </Column>
            <Column spacing={"xsmall"}>
                <Text type="b200" color="secondary">
                    <FormattedMessage id="pathSchedule.toDate" defaultMessage="To date"/>
                </Text>
                <Input type="date" value={toDate || ""}
                       onChange={(value) => setToDate(value || undefined)}
                       size="small" width={180}/>
            </Column>
            {(fromDate || toDate) &&
                <Column spacing={"xsmall"}>
                    <Button type="link" size="small" onClick={onClearDateRange}>
                        <FormattedMessage id="pathSchedule.clearDateRange" defaultMessage="Clear dates"/>
                    </Button>
                </Column>
            }
            <Column spacing={"xsmall"} alignmentHorizontal={"right"}>
                <Button type="primary" size="small" onClick={onClickAdd} disabled={!selectedCycleId}>
                    <FormattedMessage id="pathSchedule.addSchedule" defaultMessage="Add New Schedule"/>
                </Button>
            </Column>
        </Row>
    );

    return (
        <Column spacing={"medium"} spacingInset={"small none"}>
            {renderControls()}
            <PathScheduleList isLoading={isLoading}
                              error={error}
                              activeSchedule={activeSchedule}
                              scheduled={scheduled}
                              onEditActive={onEditActive}
                              onEditScheduled={onEditScheduled}
                              onActivateNow={onActivateNow}
                              onDeleteScheduled={onDeleteScheduled}/>
            {modalOpen &&
                <AddScheduleModal open={modalOpen}
                                  mode={modalMode}
                                  cycleId={selectedCycleId}
                                  stationNow={stationNow}
                                  sortPathInfoList={sortPathInfoList}
                                  sortPathSettings={sortPathSettings}
                                  initialForm={modalInitialForm}
                                  editingEffectiveDate={editingEffectiveDate}
                                  activeSchedule={activeSchedule}
                                  stagedSchedules={scheduled}
                                  isSaving={isSaving}
                                  saveError={saveError}
                                  onClose={() => setModalOpen(false)}
                                  onSave={onSave}/>
            }
        </Column>
    );
};

export default injectIntl(PathScheduleTab);
